import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DefectMarkPad, { type DefectMark } from './DefectMarkPad'
import {
  globalDefectNumber,
  marksForPhotoId,
  normalizeDefectMarks,
  setMarksForPhotoId,
} from '../utils/defectMarks'

type PhotoRef = {
  id: string
  url: string
  isCover?: boolean
}

type Props = {
  photos: PhotoRef[]
  marks: DefectMark[]
  onChange: (marks: DefectMark[] | ((prev: DefectMark[]) => DefectMark[])) => void
  activePhotoId?: string | null
  onActivePhotoChange?: (photoId: string) => void
  embedded?: boolean
}

/** Pick a listing photo and mark defects on it (stable id — unaffected by gallery reorder). */
export default function DefectMarksMultiPhoto({
  photos,
  marks,
  onChange,
  activePhotoId: controlledPhotoId,
  onActivePhotoChange,
  embedded = false,
}: Props) {
  const { t } = useTranslation()
  const [internalPhotoId, setInternalPhotoId] = useState<string | null>(photos[0]?.id ?? null)
  const activePhotoId = controlledPhotoId ?? internalPhotoId

  function setActivePhotoId(id: string) {
    if (onActivePhotoChange) onActivePhotoChange(id)
    else setInternalPhotoId(id)
  }

  useEffect(() => {
    if (photos.length === 0) {
      if (!onActivePhotoChange) setInternalPhotoId(null)
      return
    }
    if (!activePhotoId || !photos.some((p) => p.id === activePhotoId)) {
      setActivePhotoId(photos[0].id)
    }
  }, [photos, activePhotoId, onActivePhotoChange])

  useEffect(() => {
    if (photos.length === 0 || !marks.some((m) => !m.photo_id)) return
    onChange(normalizeDefectMarks(marks, photos))
  }, [marks, photos, onChange])

  if (photos.length === 0) {
    return <p className="meta-text">{t('listing.defectMarkNeedPhoto')}</p>
  }

  const activePhoto = photos.find((p) => p.id === activePhotoId) ?? photos[0]
  const activeMarks = marksForPhotoId(marks, activePhoto.id)
  const activeIdx = photos.findIndex((p) => p.id === activePhoto.id)
  const activeNo = activeIdx + 1

  function patchMarks(next: DefectMark[]) {
    const photoId = activePhoto.id
    onChange((prev) => setMarksForPhotoId(prev, photoId, next))
  }

  function stepPhoto(delta: number) {
    const nextIdx = (activeIdx + delta + photos.length) % photos.length
    setActivePhotoId(photos[nextIdx].id)
  }

  return (
    <div className="defect-mark-multi">
      <div className="defect-mark-photos defect-mark-photos--bar" role="tablist" aria-label={t('listing.defectMarkPhotoPicker')}>
        {photos.length > 1 ? (
          <button
            type="button"
            className="defect-mark-photos__nav"
            onClick={() => stepPhoto(-1)}
            aria-label={t('listing.defectMarkPhotoPrev')}
          >
            ‹
          </button>
        ) : null}
        <div className="defect-mark-photos__strip">
          {photos.map((photo) => {
            const count = marksForPhotoId(marks, photo.id).length
            return (
              <button
                key={photo.id}
                type="button"
                role="tab"
                aria-selected={photo.id === activePhoto.id}
                className={`defect-mark-photos__thumb${photo.id === activePhoto.id ? ' defect-mark-photos__thumb--active' : ''}`}
                onClick={() => setActivePhotoId(photo.id)}
              >
                <img src={photo.url} alt="" draggable={false} />
                {photo.isCover ? (
                  <span className="defect-mark-photos__cover">{t('listing.photoPrimaryPreview')}</span>
                ) : null}
                {count > 0 ? (
                  <span className="defect-mark-photos__count" aria-hidden="true">
                    {count}
                  </span>
                ) : null}
              </button>
            )
          })}
        </div>
        {photos.length > 1 ? (
          <button
            type="button"
            className="defect-mark-photos__nav"
            onClick={() => stepPhoto(1)}
            aria-label={t('listing.defectMarkPhotoNext')}
          >
            ›
          </button>
        ) : null}
      </div>
      <p className="meta-text defect-mark-multi__hint">
        {photos.length > 1
          ? t('listing.defectMarkPhotoHint', { n: activeNo, total: photos.length })
          : t('listing.defectMarkHint')}
      </p>
      <DefectMarkPad
        embedded={embedded}
        imageUrl={activePhoto.url}
        marks={activeMarks}
        onChange={patchMarks}
        getMarkNumber={(mark) => globalDefectNumber(marks, mark, photos)}
      />
      {marks.length > 0 ? (
        <p className="meta-text defect-mark-multi__total">
          {t('listing.defectMarkTotal', { count: marks.length })}
        </p>
      ) : null}
    </div>
  )
}
